import { Capacitor } from '@capacitor/core'
import { Preferences } from '@capacitor/preferences'
import { defineStore } from 'pinia'

const CONSENT_KEY = 'glouton_privacy_consent'
const CONSENT_VERSION = 2

interface StoredConsent {
  version: number
  googleAnalytics: boolean
  advertising: boolean
  decidedAt: string
  platform: string
}

export const useConsentStore = defineStore('consent', () => {
  const googleAnalytics = ref(false)
  const advertising = ref(false)
  const hasDecision = ref(false)
  const initialized = ref(false)
  const decidedAt = ref<string | null>(null)
  const automatedMode = ref(false)
  const preferencesOpen = ref(false)
  const preferencesScreen = ref<'summary' | 'customize'>('summary')

  const platform = computed(() => Capacitor.getPlatform())
  const googleAnalyticsAllowed = computed(() => !automatedMode.value && googleAnalytics.value)
  const advertisingAllowed = computed(() => !automatedMode.value && advertising.value)

  async function readStoredConsent(): Promise<string | null> {
    if (Capacitor.isNativePlatform()) {
      const result = await Preferences.get({ key: CONSENT_KEY })
      return result.value
    }

    return localStorage.getItem(CONSENT_KEY)
  }

  async function writeStoredConsent(value: string): Promise<void> {
    if (Capacitor.isNativePlatform()) {
      await Preferences.set({ key: CONSENT_KEY, value })
      return
    }

    localStorage.setItem(CONSENT_KEY, value)
  }

  async function applyNativeAdvertisingConsent(enabled: boolean): Promise<void> {
    if (!Capacitor.isNativePlatform())
      return

    const { AdManager } = await import('~/utils/ad-manager')
    await AdManager.configureAdvertisingConsent({
      personalizedAds: enabled,
      requestTrackingAuthorization: enabled && platform.value === 'ios',
    })
  }

  async function initialize(options: { automated?: boolean } = {}): Promise<void> {
    if (initialized.value || !import.meta.client)
      return

    automatedMode.value = options.automated === true

    try {
      const storedValue = await readStoredConsent()
      if (storedValue) {
        const stored = JSON.parse(storedValue) as StoredConsent
        if (
          stored.version === CONSENT_VERSION
          && typeof stored.googleAnalytics === 'boolean'
          && typeof stored.advertising === 'boolean'
          && typeof stored.decidedAt === 'string'
        ) {
          googleAnalytics.value = stored.googleAnalytics
          advertising.value = stored.advertising
          decidedAt.value = stored.decidedAt
          hasDecision.value = true
        }
      }
    }
    catch (error) {
      console.warn('Unable to read privacy preferences:', error)
    }
    finally {
      initialized.value = true
    }

    try {
      await applyNativeAdvertisingConsent(advertisingAllowed.value)
    }
    catch (error) {
      console.warn('Unable to apply native advertising consent:', error)
    }

    if (!hasDecision.value && !automatedMode.value)
      openSummary()
  }

  async function saveConsent(choices: { googleAnalytics: boolean, advertising: boolean }): Promise<void> {
    const timestamp = new Date().toISOString()
    const consent: StoredConsent = {
      version: CONSENT_VERSION,
      googleAnalytics: choices.googleAnalytics,
      advertising: choices.advertising,
      decidedAt: timestamp,
      platform: platform.value,
    }

    googleAnalytics.value = choices.googleAnalytics
    advertising.value = choices.advertising
    decidedAt.value = timestamp
    hasDecision.value = true
    await writeStoredConsent(JSON.stringify(consent))

    try {
      await applyNativeAdvertisingConsent(advertisingAllowed.value)
    }
    catch (error) {
      console.warn('Unable to apply native advertising consent:', error)
    }

    closePreferences()
  }

  async function setAutomatedMode(enabled: boolean): Promise<void> {
    if (automatedMode.value === enabled)
      return

    automatedMode.value = enabled
    if (enabled) {
      closePreferences()
    }
    else if (!hasDecision.value) {
      openSummary()
    }

    try {
      await applyNativeAdvertisingConsent(advertisingAllowed.value)
    }
    catch (error) {
      console.warn('Unable to apply native advertising consent:', error)
    }
  }

  function openSummary(): void {
    if (automatedMode.value)
      return
    preferencesScreen.value = 'summary'
    preferencesOpen.value = true
  }

  function openCustomize(): void {
    if (automatedMode.value)
      return
    preferencesScreen.value = 'customize'
    preferencesOpen.value = true
  }

  function closePreferences(): void {
    preferencesOpen.value = false
  }

  return {
    googleAnalytics,
    advertising,
    googleAnalyticsAllowed,
    advertisingAllowed,
    hasDecision,
    initialized,
    decidedAt,
    automatedMode,
    preferencesOpen,
    preferencesScreen,
    initialize,
    saveConsent,
    setAutomatedMode,
    openSummary,
    openCustomize,
    closePreferences,
  }
})
